/**
 * Estimated Delivery Badge
 *
 * Reads delivery day ranges from a sibling <script type="application/json"> tag,
 * calculates date range from today, and injects formatted dates into the badge.
 *
 * Full format:  "Tuesday, 3rd June 2026 to Thursday, 5th June 2026"
 * Short format: "3 Jun – 5 Jun"
 */

const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTH_NAMES_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];
const MONTH_NAMES_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

/**
 * Returns the ordinal suffix for a given day number (1 -> "st", 2 -> "nd", etc.)
 * @param {number} day
 * @returns {string}
 */
function getOrdinalSuffix(day) {
  const remainder10 = day % 10;
  const remainder100 = day % 100;

  if (remainder10 === 1 && remainder100 !== 11) return 'st';
  if (remainder10 === 2 && remainder100 !== 12) return 'nd';
  if (remainder10 === 3 && remainder100 !== 13) return 'rd';
  return 'th';
}

/**
 * Adds a number of days to a date, returning a new Date object.
 * @param {Date} date
 * @param {number} days
 * @returns {Date}
 */
function addDays(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * Formats a date in full format: "Weekday, Nth Month YYYY"
 * @param {Date} date
 * @returns {string}
 */
function formatFull(date) {
  const weekday = WEEKDAY_NAMES[date.getDay()];
  const day = date.getDate();
  const ordinal = getOrdinalSuffix(day);
  const month = MONTH_NAMES_FULL[date.getMonth()];
  const year = date.getFullYear();
  return `${weekday}, ${day}${ordinal} ${month} ${year}`;
}

/**
 * Formats a date in short format: "D Mon"
 * @param {Date} date
 * @returns {string}
 */
function formatShort(date) {
  const day = date.getDate();
  const month = MONTH_NAMES_SHORT[date.getMonth()];
  return `${day} ${month}`;
}

/**
 * Finds all delivery badge elements on the page and populates date strings.
 */
function initDeliveryBadges() {
  const badges = document.querySelectorAll('[data-estimated-delivery]');

  for (const badge of badges) {
    const configScript = badge.nextElementSibling;
    if (!configScript || configScript.getAttribute('type') !== 'application/json') continue;

    let config;
    try {
      config = JSON.parse(configScript.textContent);
    } catch {
      continue;
    }

    const { minDays, maxDays, short } = config;
    if (minDays == null || maxDays == null) continue;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const startDate = addDays(today, minDays);
    const endDate = addDays(today, maxDays);

    const datesEl = badge.querySelector('[data-delivery-dates]');
    if (!datesEl) continue;

    if (short) {
      datesEl.textContent = `${formatShort(startDate)} – ${formatShort(endDate)}`;
    } else {
      datesEl.textContent = `${formatFull(startDate)} to ${formatFull(endDate)}`;
    }

    datesEl.removeAttribute('aria-label');
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDeliveryBadges);
} else {
  initDeliveryBadges();
}

document.addEventListener('estimated-delivery:refresh', initDeliveryBadges);

// Re-run whenever any DOM mutation leaves empty [data-delivery-dates] spans.
// This covers: modal reopen, cart drawer show, AJAX morph, innerHTML swap --
// any case where the badge container exists but dates haven't been filled in yet.
const deliveryObserver = new MutationObserver(function() {
  if (document.querySelector('[data-delivery-dates]:empty')) {
    initDeliveryBadges();
  }
});

deliveryObserver.observe(document.body, { childList: true, subtree: true });
